import React from "react";
import PropTypes from "prop-types";
import {withStyles} from "@material-ui/core/styles";
import Widget from "./Widget";
import CustomColorPicker from "./CustomColorPicker";
import LocalizedStrings from "react-localization";
import {ExpandMore, Save, Close, OpenInNew, ContentCopy, HelpOutline} from "@material-ui/icons/";
import {Editor} from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {convertToRaw, convertFromRaw, EditorState} from "draft-js";
import Colors from "./CustomColors";
import * as Utility from "../components/Utility";

import {
    Button, Input, InputAdornment, InputLabel, MenuItem, Select, Switch, TextField, Card,
    CardContent, Grid, FormControl, FormGroup, FormControlLabel, Typography, ExpansionPanel, ExpansionPanelSummary,
    ExpansionPanelDetails, Tooltip, Snackbar, IconButton, CardActions, Dialog, Slide, DialogActions, DialogContent,
    DialogContentText, DialogTitle
} from "@material-ui/core/index";

const styles = ({
    root: {
        flexGrow: 1,
    },
    formControl: {
        width: "100%",
    },
    textField: {
        marginBottom: 16,
    },
    editor: {
        maxWidth: 470,
    },
    messenger: {
        maxWidth: 470,
    },
    fixedContainer: {
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 2,
    },
    fabMini: {
        marginLeft: "calc(100% - 40px)",
    },
    select: {
        width: "100%",
    },
    spacer12: {
        height: 12,
    },
    spacer16: {
        height: 16,
    },
    colorPicker: {
        touchAction: "none",
    },
    switchMessengerVisible: {
        marginTop: -8,
        marginBottom: 8,
    },
    switchPrivacyVisible: {
        marginTop: -8,
        marginBottom: 8,
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",
    },
    cardActions: {
        display: "flex",
        justifyContent: "flex-end",
        paddingBottom: 24,
    },
    colorCaption: {
        marginBottom: 12,
    },
    draftEditorContainer: {
        marginBottom: 16,
        marginTop: 8,
        height: 229,
    },
    draftEditorWrapper: {
        height: "100%",
        border: "1px solid " + Colors.gray,
    },
    draftEditor: {
        height: "calc(100% - 51px)",
        overflow: "auto",
    },
    widgetPreviewHeading: {
        textAlign: "center",
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: "1px solid " + Colors.gray,
    },
    inputAdornmentIconButton: {
        width: 32,
        height: 32,
    }
});

const strings = new LocalizedStrings({
    en: {
        editor_add_widget: "Add Widget",
        editor_add: "Add",
        editor_checkbox: "Checkbox",
        editor_checkbox_hint: "Checkbox Hint",
        editor_checkbox_text: "Checkbox Text",
        editor_color: "Color",
        editor_description: "Description",
        editor_general: "General",
        editor_widget_language: "Widget Language",
        editor_widget_language_german: "German",
        editor_widget_language_english: "English",
        editor_widget_language_french: "French",
        editor_widget_language_spanish: "Spanish",
        editor_widget_language_portuguese: "Portuguese",
        editor_widget_language_italian: "Italian",
        editor_header: "Header",
        editor_heading: "Heading",
        editor_menu: "Menu",
        editor_menu_item: "Menu Item",
        editor_url: "URL",
        editor_messenger: "Messenger",
        editor_name: "Name",
        editor_select: "Select",
        editor_settings: "Settings",
        editor_subheading: "Subheading",
        editor_tabs: "Tabs",
        editor_text: "Text",
        editor_text_color: "Text Color",
        editor_visible: "Visible",
        editor_widget: "Widget",
        editor_width: "Width",
        editor_width_info: "Min width: 320px | Max width: 800px | Min height: 300px",
        editor_height: "Height",
        editor_save: "Save",
        editor_saved: "Saved!",
        editor_menu_item_new: "New Menu Item",
        editor_widget_new: "New Widget",
        editor_delete: "Delete",
        editor_embed_code: "Embed Code",
        editor_border: "Border",
        editor_appearance: "Appearance",
        editor_background_color: "Background Color",
        editor_privacy: "Privacy",
        editor_privacy_statement: "Privacy Statement",
        editor_type_of_number_distribution: "Type of number distribution",
        editor_number_distribution_vcard: "Download as vCard",
        editor_number_distribution_direct: "Link number directly",
        editor_number_distribution_link: "WhatsApp-Link",
        editor_number_distribution_link_desktop: "WhatsApp-Link (also Desktop)",
        editor_open_new_tab: "Open in new tab",
        editor_copy: "Copy",
        editor_start_message: "Start Message",
        editor_button_text: "Button Text",
        editor_copy_success: "Copied!",
        editor_copy_fail: "Failed to copy!",
        editor_set_privacy_statement: "Please set the privacy statement URL!",
        editor_widget_preview: "Widget Preview",
        editor_help: "Help",
        editor_wysiwyg_help:
            "You can use the following variables in the editor:\n" +
            "%number% = Current Phonenumber\n" +
            "%start% = Start Message\n" +
            "%facebook% = Facebook Username\n" +
            "%telegram% = Telegram Username",
        editor_ok: "OK",
        editor_whatsapp_link_preview: "The \"WhatsApp-Link\" is normally displayed only on mobile devices. " +
            "Do you like to use it anyway?",
        editor_desktop: "Desktop",
        editor_mobile: "Smartphone",
        editor_desktop_mobile_text: "Desktop / Smartphone Description",
    }
});

class CustomEditorOption extends React.Component {
    render() {
        return (
            <div onClick={this.props.openDialog}
                 style={{
                     paddingLeft: 10,
                     paddingBottom: 6,
                     display: "flex",
                     flexDirection: "column",
                     justifyContent: "center",
                 }}>
                <IconButton component="span" style={{width: 0, height: 0}}>
                    <HelpOutline/>
                </IconButton>
            </div>
        );
    }
}

class WidgetEditor extends React.Component {

    /* ------ LIFECYCLE -------- */

    constructor(props) {
        super(props);

        switch (props.language) {
            case "de":
                strings.setContent({de: props.translation});
                break;
            case "en":
                strings.setContent({en: props.translation});
                break;
            case "fr":
                strings.setContent({fr: props.translation});
                break;
            case "es":
                strings.setContent({es: props.translation});
                break;
            case "pt":
                strings.setContent({pt: props.translation});
                break;
            case "it":
                strings.setContent({it: props.translation});
                break;
            default:
                console.warn("No language specified!");
                break;
        }
        strings.setLanguage(props.language);

        let userHasSavedWidget = false;
        let defaultWidget = JSON.parse(props.defaultData.data[0].widget);
        let widgets = props.widgets;

        if (!widgets) {
            widgets = [];
            let copyrightName = "MessengerPeople";
            let copyrightLink = "https://www.messengerpeople.com/de/";
            if (props.messengerData.aboutname !== null && props.messengerData.abouturl !== null) {
                copyrightName = props.messengerData.aboutname;
                copyrightLink = props.messengerData.abouturl;
                defaultWidget.config.menuItems.pop();
                defaultWidget.config.menuItems.push({id: 0, name: copyrightName, url: copyrightLink});
            }
            if (props.messengerData.heading !== "") {
                defaultWidget.config.content.heading = props.messengerData.heading;
            }
            if (props.messengerData.subheading !== "") {
                defaultWidget.config.content.subHeading = props.messengerData.subheading;
            }
            if (props.messengerData.adv_needed !== "1") {
                defaultWidget.config.checkbox.visible = false;
            }
            widgets.push(defaultWidget);
        } else {
            userHasSavedWidget = true;
        }

        this.state = {
            widget: 0,
            messenger: 0,
            menuItem: 0,
            waDesktopMobile: 1,
            isSnackbarOpen: false,
            isHelpDialogOpen: false,
            expanded: "panelGeneral",
            snackbarText: "",
            snackbarAutoHideDuration: 3000,
            appearanceCollapseProps: {style: {overflow: "hidden", zIndex: 2}},

            language: props.language,
            apiKey: props.apiKey,
            widgetHash: props.widgetHash,
            phoneNumbers: props.phoneNumbers,
            messengerData: props.messengerData,
            wbdomain: props.wbdomain,

            userHasSavedWidget: userHasSavedWidget,
            defaultWidget: Object.assign({}, defaultWidget),
            widgets: widgets,
            config: widgets[0].config,
            messengerColor: widgets[0].config.messenger[0].color,
            messengerTextColor: widgets[0].config.messenger[0].textColor,
            mainBackgroundColor: widgets[0].config.appearance.backgroundColor,
            mainTextColor: widgets[0].config.appearance.textColor,
            numberDistribution: widgets[0].config.messenger[0].numberDistribution,
            editorState: EditorState.createWithContent(convertFromRaw(
                widgets[0].config.messenger[0].descriptions[widgets[0].config.messenger[0].numberDistribution])),
            editorIndex: widgets[0].config.messenger[0].numberDistribution,
        };
    };

    /* ------ HANDLER -------- */

    handleSwitchChange = name => (event, checked) => {
        let tmpConfig = Object.assign({}, this.state.config);
        switch (name) {
            case "tabs":
                tmpConfig.appearance.tabs = checked;
                break;
            case "checkbox":
                tmpConfig.checkbox.visible = checked;
                break;
            case "companyText":
                tmpConfig.appearance.companyText = checked;
                break;
            case "border":
                tmpConfig.appearance.border = checked;
                break;
            case "messengerVisible":
                tmpConfig.messenger[this.state.messenger].visible = checked;
                break;
            default:
                // Tell the developer that something is missing
                console.warn("No name supplied for switch!");
                break;
        }
        this.setState({config: tmpConfig});
    };

    handleTextFieldChange = (section, setting, type) => event => {
        let value = event.target.value;
        let tmpConfig = Object.assign({}, this.state.config);

        switch (type) {
            case "int":
                value = parseInt(value, 10);
                break;
            default:
                // No type given so nothing to do for us
                break;
        }

        switch (section) {
            case "widget":
                let tmpWidgets = this.state.widgets.slice(0);
                tmpWidgets[this.state.widget].name = event.target.value;
                this.setState({widgets: tmpWidgets});
                break;
            case "content":
                tmpConfig.content[setting] = value;
                break;
            case "appearance":
                if (value > 1000) {
                    tmpConfig.appearance[setting] = 1000;
                } else {
                    tmpConfig.appearance[setting] = value;
                }
                break;
            case "checkbox":
                tmpConfig.checkbox[setting] = value;
                break;
            case "menuItems":
                tmpConfig.menuItems[this.state.menuItem][setting] = value;
                break;
            case "messenger":
                switch (setting) {
                    case "color":
                        this.setState({messengerColor: value});
                        if (WidgetEditor.isColorValid(value)) {
                            tmpConfig.messenger[this.state.messenger][setting] = value;
                        }
                        break;
                    case "textColor":
                        this.setState({messengerTextColor: value});
                        if (WidgetEditor.isColorValid(value)) {
                            tmpConfig.messenger[this.state.messenger][setting] = value;
                        }
                        break;
                    case "buttonText":
                        tmpConfig.messenger[this.state.messenger]
                            .distributionButtonTexts[this.state.numberDistribution] = value;
                        break;
                    case "numberDistributionStartMessages":
                        let numberDistribution = this.state.numberDistribution;

                        if (this.state.waDesktopMobile === 2) {
                            numberDistribution += 1;
                        }

                        tmpConfig.messenger[this.state.messenger]
                            .numberDistributionStartMessages[numberDistribution] = value;
                        break;
                    default:
                        tmpConfig.messenger[this.state.messenger][setting] = value;
                        break;
                }
                break;
            case "privacy":
                tmpConfig.privacy[setting] = value;
                break;
            default:
                // Tell the developer that he f***** up
                console.warn("No config mapping for setting: " + setting + " in section: " + section);
                break;
        }
        this.setState({config: tmpConfig});
    };

    handleSelectChange = name => event => {
        let newValue = event.target.value;
        switch (name) {
            case "widget":
                let newNumberDistribution = this.state.widgets[newValue].config.messenger[0].numberDistribution;
                this.setState({
                    widget: newValue,
                    messenger: 0,
                    config: this.state.widgets[newValue].config,
                    messengerColor: this.state.widgets[newValue].config.messenger[0].color,
                    messengerTextColor: this.state.widgets[newValue].config.messenger[0].textColor,
                    numberDistribution: newNumberDistribution,
                    editorState: EditorState.createWithContent(convertFromRaw(this.state.widgets[newValue]
                        .config.messenger[0].descriptions[1])),
                }, this.forceUpdate);
                break;
            case "messenger":
                newNumberDistribution = this.state.config.messenger[newValue].numberDistribution;

                let editorText = this.state.config.messenger[newValue]
                    .descriptions[newNumberDistribution];

                if (!editorText["entityMap"]) {
                    editorText["entityMap"] = {};
                }

                this.setState({
                    messenger: newValue,
                    messengerColor: this.state.config.messenger[newValue].color,
                    messengerTextColor: this.state.config.messenger[newValue].textColor,
                    numberDistribution: newNumberDistribution,
                    editorIndex: newNumberDistribution,
                    editorState: EditorState.createWithContent(convertFromRaw(editorText)),
                }, this.forceUpdate);
                break;
            case "menuItem":
                this.setState({menuItem: newValue});
                break;
            case "numberDistribution":
                let tmpConfig = Object.assign({}, this.state.config);
                tmpConfig.messenger[this.state.messenger].numberDistribution = newValue;
                this.setState({
                    config: tmpConfig,
                    numberDistribution: newValue,
                    editorIndex: newValue,
                    waDesktopMobile: this.state.messenger === 0 ? 1 : newValue,
                    editorState: EditorState.createWithContent(convertFromRaw(tmpConfig.messenger[this.state.messenger]
                        .descriptions[newValue])),
                });
                break;
            case "waDesktopMobile":
                this.setState({
                    waDesktopMobile: newValue,
                    editorIndex: newValue,
                    editorState: EditorState.createWithContent(convertFromRaw(this.state.config
                        .messenger[this.state.messenger].descriptions[newValue]))
                });
                break;
            default:
                console.warn("No name supplied for select!");
                break;
        }
    };

    handleExpansionChange = panel => (event, expanded) => {
        this.setState({expanded: expanded ? panel : false});
        if (panel === "panelAppearance") {
            setTimeout(() => {
                this.setState({appearanceCollapseProps: {style: {overflow: expanded ? "visible" : "hidden"}}});
            }, expanded ? 300 : 0);
        } else {
            this.setState({appearanceCollapseProps: {style: {overflow: "hidden"}}});
        }
    };

    handleAddMenuItemClick = () => {
        let tmpConfig = Object.assign({}, this.state.config);
        let emptyMenuItem = {
            id: tmpConfig.menuItems.length,
            name: strings.editor_menu_item_new,
            url: "/",
        };
        tmpConfig.menuItems.push(emptyMenuItem);
        this.setState({config: tmpConfig, menuItem: tmpConfig.menuItems.length - 1});
    };

    handleDeleteMenuItemClick = () => {
        let cleanMenuItems = [];
        let tmpConfig = Object.assign({}, this.state.config);
        tmpConfig.menuItems.splice(this.state.menuItem, 1);
        tmpConfig.menuItems.forEach((element, index) => {
            element.id = index;
            cleanMenuItems.push(element);
        });
        tmpConfig.menuItems = cleanMenuItems;
        this.setState({config: tmpConfig, menuItem: tmpConfig.menuItems.length - 1}, this.forceUpdate);
    };

    handleColorPickerChangeComplete = name => color => {
        let upperCaseColor = color.hex.toUpperCase();
        let tmpConfig = Object.assign({}, this.state.config);
        switch (name) {
            case "mainBackgroundColor":
                tmpConfig.appearance.backgroundColor = upperCaseColor;
                this.setState({mainBackgroundColor: upperCaseColor});
                break;
            case "mainTextColor":
                tmpConfig.appearance.textColor = upperCaseColor;
                this.setState({mainTextColor: upperCaseColor});
                break;
            case "color":
                tmpConfig.messenger[this.state.messenger].color = upperCaseColor;
                this.setState({messengerColor: upperCaseColor});
                break;
            case "textColor":
                tmpConfig.messenger[this.state.messenger].textColor = upperCaseColor;
                this.setState({messengerTextColor: upperCaseColor});
                break;
            default:
                console.warn("No colorpicker name given!");
                break;
        }
        this.setState({config: tmpConfig});
    };

    handleAddWidgetClick = () => {
        let tmpWidgets = this.state.widgets.slice(0);
        let defaultWidget = Object.assign({}, this.state.defaultWidget);
        defaultWidget.id = tmpWidgets.length;
        tmpWidgets.push(defaultWidget);
        this.setState({
            widgets: tmpWidgets,
        }, () => {
            this.setState({
                messengerColor: this.state.widgets[this.state.widgets.length - 1].config.messenger[0].color,
                messengerTextColor: this.state.widgets[this.state.widgets.length - 1].config.messenger[0].textColor,
                config: this.state.widgets[this.state.widgets.length - 1].config,
                widget: this.state.widgets.length - 1,
                editorState: EditorState.createWithContent(
                    convertFromRaw(this.state.widgets[this.state.widgets.length - 1]
                        .config.messenger[this.state.messenger].descriptions[this.state.numberDistribution])),
            }, this.forceUpdate)
        });
    };

    handleDeleteWidgetClick = () => {
        let cleanWidgets = [];
        let tmpWidgets = this.state.widgets.slice(0);
        tmpWidgets.splice(this.state.widget, 1);
        tmpWidgets.forEach((element, index) => {
            element.id = index;
            cleanWidgets.push(element);
        });
        this.setState({
            widgets: cleanWidgets,
            widget: 0,
            config: cleanWidgets[0].config,
            messengerColor: cleanWidgets[0].config.messenger[0].color,
            messengerTextColor: cleanWidgets[0].config.messenger[0].textColor,
        }, this.forceUpdate);
    };

    handleWidgetSaveClick = () => {
        if (this.state.expanded === "panelAppearance") {
            this.setState({appearanceCollapseProps: {style: {overflow: "hidden"}}});
        }

        const statementUrl = this.state.config.privacy.statementUrl;
        if (statementUrl === "") {
            this.setState({
                expanded: "panelPrivacy",
                snackbarText: strings.editor_set_privacy_statement,
                snackbarAutoHideDuration: 1000 * 60 * 60, // One hour should be enough
                isSnackbarOpen: true,
            }, () => {
                this.setState({snackbarAutoHideDuration: 3000})
            });
            return;
        }

        let widgets = this.state.widgets.slice(0);
        let widgetObject = {widgets};
        let formData = new FormData();
        formData.append("apikey", this.state.apiKey);
        formData.append("widget_info", JSON.stringify(widgetObject));
        formData.append("widget_hash", this.state.widgetHash);
        let requestOptions = {
            method: "POST",
            body: formData,
        };
        let request = new Request("https://rest.messengerpeople.com/api/v8/widget_store", requestOptions);
        fetch(request)
            .then(res => res.json())
            .then(
                (result) => {
                    if (!result.success) {
                        this.setState({snackbarText: "Something went wrong!"}, () => {
                            this.setState({isSnackbarOpen: true});
                        });
                        return;
                    }
                    this.setState({snackbarText: strings.editor_saved}, () => {
                        this.setState({
                            isSnackbarOpen: true,
                            userHasSavedWidget: true,
                        });
                    })
                },
                (error) => {
                    this.setState({snackbarText: "Something went wrong!"}, () => {
                        this.setState({isSnackbarOpen: true});
                        console.error(error);
                    })
                }
            );
    };

    handleSnackbarClose = () => {
        this.setState({isSnackbarOpen: false})
    };

    handleEditorStateChange = (editorState) => {
        this.setState({editorState: editorState});
        let rawContent = convertToRaw(editorState.getCurrentContent());
        let tmpConfig = Object.assign({}, this.state.config);
        tmpConfig.messenger[this.state.messenger].descriptions[this.state.editorIndex] = rawContent;
        this.setState({config: tmpConfig});
    };

    handleOpenWidget = () => {
        window.open("https://wb.messengerpeople.com/?widget_hash=" +
            `${this.state.widgetHash}&lang=${this.state.language}&wn=${this.state.widget}&pre=1`);
    };

    handleCopy = () => {
        let snackbarText = strings.editor_copy_success;

        navigator.clipboard.writeText(this.getEmbeddingLink()).then(() => {
        }, function (error) {
            snackbarText = strings.editor_copy_fail + " " + error;
        });

        this.setState({snackbarText: snackbarText}, () => {
            this.setState({isSnackbarOpen: true});
        });
    };

    handleOpenHelpcenter = () => {
        let link;
        switch (this.state.language) {
            case "de":
                link = "https://helpcenter.messengerpeople.com/de/dokumentationen/einstellungen/design/widget-editor";
                break;
            default:
                link = "https://www.messengerpeople.com/new-gdpr-friendly-widget/";
                break;
        }
        window.open(link);
    };

    handleOpenDialog = () => {
        this.setState({isHelpDialogOpen: true});
    };

    handleDialogClose = () => {
        this.setState({isHelpDialogOpen: false});
    };

    /* ------ HTML -------- */

    getHelpDialog() {
        let lines = strings.editor_wysiwyg_help.split("\n");

        return (
            <Dialog
                open={this.state.isHelpDialogOpen}
                TransitionComponent={Slide}
                keepMounted
                onClose={this.handleDialogClose}
            >
                <DialogTitle>
                    {strings.editor_help}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        {lines.map((element, index) => (
                            <p key={index}>{element}</p>
                        ))}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleDialogClose} color="primary">
                        {strings.editor_ok}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    getEmbeddingLink() {
        let copyrightName = "MessengerPeople";
        let copyrightLink = "https://www.messengerpeople.com/";
        if (this.state.messengerData.aboutname !== null && this.state.messengerData.abouturl !== null) {
            copyrightName = this.state.messengerData.aboutname;
            copyrightLink = this.state.messengerData.abouturl;
        }

        return (
            `<div style='width:${this.state.config.appearance.width + 10}px;height:${this.state.config.appearance.height}px'><iframe rel='noopener noreferrer' src='https://${this.state.wbdomain}/?widget_hash=${this.state.widgetHash}&lang=${this.state.language}&wn=${this.state.widget}' style="border:none;height:100%;width:100%;"></iframe><a href='${copyrightLink}' target="_blank" style="margin-top:-10px;padding-top: 6px\\9;text-decoration:none;text-align:right;display:block;font-size:11px;padding-right:8px;color: rgba(8, 0, 0, 0.54);font-family:'Roboto',sans-serif;">© ${copyrightName}</a></div>`
        );
    };

    getSnackbar() {
        const {classes} = this.props;

        return (
            <Snackbar
                anchorOrigin={{vertical: "bottom", horizontal: "center",}}
                open={this.state.isSnackbarOpen}
                autoHideDuration={this.state.snackbarAutoHideDuration}
                onClose={this.handleSnackbarClose}
                message={<span id="messageId">{this.state.snackbarText}</span>}
                action={[
                    <IconButton
                        key="close"
                        color="primary"
                        className={classes.snackbar}
                        onClick={this.handleSnackbarClose}>
                        <Close/>
                    </IconButton>
                ]}
            />
        );
    };

    getLeftSidebar() {
        const {classes} = this.props;

        return (
            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="display1" gutterBottom color="primary">
                        {strings.editor_widget}
                        <IconButton component="span" style={{float: "right", height: 40, width: 40}}
                                    onClick={this.handleOpenHelpcenter}>
                            <HelpOutline/>
                        </IconButton>
                    </Typography>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <FormControl className={classes.select}>
                                <InputLabel htmlFor="selectWidget">
                                    {strings.editor_select}
                                </InputLabel>
                                <Select value={this.state.widget} onChange={this.handleSelectChange("widget")}
                                        inputProps={{name: "widget", id: "selectWidget",}}>
                                    {this.state.widgets.map(element => (
                                        <MenuItem key={element.id} value={element.id}>
                                            {element.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </FormControl>
                    <div className={classes.spacer16}/>
                    {this.getExpansionPanelGeneral()}
                    {this.getExpansionPanelAppearance()}
                    {this.getExpansionPanelHeader()}
                    {this.getExpansionPanelDescription()}
                    {this.getExpansionPanelPrivacy()}
                    {this.getExpansionPanelMenu()}
                    <div className={classes.spacer16}/>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <FormControl>
                                <InputLabel htmlFor="adornment-embedLink">{strings.editor_embed_code}</InputLabel>
                                <Input id="adornment-embedLink"
                                       readOnly
                                       disabled={this.state.config.privacy.statementUrl.length === 0}
                                       endAdornment={
                                           <InputAdornment position="end">
                                               <Tooltip title={strings.editor_open_new_tab} placement="top">
                                                   <div>
                                                       <IconButton
                                                           onClick={this.handleOpenWidget}
                                                           className={classes.inputAdornmentIconButton}
                                                           disabled={!this.state.userHasSavedWidget}
                                                       >
                                                           <OpenInNew/>
                                                       </IconButton>
                                                   </div>
                                               </Tooltip>
                                               <Tooltip title={strings.editor_copy} placement="top">
                                                   <div>
                                                       <IconButton
                                                           onClick={this.handleCopy}
                                                           className={classes.inputAdornmentIconButton}
                                                           disabled={!this.state.userHasSavedWidget}
                                                       >
                                                           <ContentCopy/>
                                                       </IconButton>
                                                   </div>
                                               </Tooltip>
                                           </InputAdornment>
                                       }
                                       value={
                                           this.state.config.privacy.statementUrl.length === 0 ?
                                               strings.editor_set_privacy_statement :
                                               this.getEmbeddingLink()
                                       }
                                />
                            </FormControl>
                        </FormGroup>
                    </FormControl>
                </CardContent>
                <CardActions className={classes.cardActions}>
                    <Button color="primary" onClick={this.handleDeleteWidgetClick}
                            disabled={!(this.state.widgets.length > 1)}>
                        {strings.editor_delete}
                    </Button>
                    <Button color="primary" onClick={this.handleAddWidgetClick}>
                        {strings.editor_add_widget}
                    </Button>
                </CardActions>
            </Card>
        );
    };

    getExpansionPanelGeneral() {
        const {classes} = this.props;

        return (
            <ExpansionPanel
                expanded={this.state.expanded === "panelGeneral"}
                onChange={this.handleExpansionChange("panelGeneral")}
            >
                <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="headline" className={classes.heading}>
                        {strings.editor_general}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <TextField
                                label={strings.editor_name}
                                value={this.state.widgets[this.state.widget].name}
                                onChange={this.handleTextFieldChange("widget", "name")}
                                className={classes.textField}
                            />
                            <FormControl className={classes.textField}>
                                <InputLabel htmlFor="adornment-height">{strings.editor_height}</InputLabel>
                                <Input
                                    value={this.state.config.appearance.height}
                                    onChange={this.handleTextFieldChange("appearance", "height", "int")}
                                    type="number"
                                    inputProps={{max: 1000}}
                                    endAdornment={<InputAdornment position="end">px</InputAdornment>}
                                />
                            </FormControl>
                            <FormControl className={classes.textField}>
                                <InputLabel htmlFor="adornment-width">{strings.editor_width}</InputLabel>
                                <Input
                                    value={this.state.config.appearance.width}
                                    onChange={this.handleTextFieldChange("appearance", "width", "int")}
                                    type="number"
                                    inputProps={{max: 1000}}
                                    endAdornment={<InputAdornment position="end">px</InputAdornment>}
                                />
                            </FormControl>
                        </FormGroup>
                    </FormControl>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    };

    getExpansionPanelAppearance() {
        const {classes} = this.props;

        return (
            <ExpansionPanel
                expanded={this.state.expanded === "panelAppearance"}
                onChange={this.handleExpansionChange("panelAppearance")}
                CollapseProps={this.state.appearanceCollapseProps}
            >
                <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="headline" className={classes.heading}>
                        {strings.editor_appearance}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <FormControlLabel control={
                                <Switch
                                    checked={this.state.config.appearance.tabs}
                                    onChange={this.handleSwitchChange("tabs")}
                                    value="switchTabs"
                                    color="primary"
                                />
                            } label={strings.editor_tabs}/>
                            <FormControlLabel control={
                                <Switch
                                    checked={this.state.config.appearance.border}
                                    onChange={this.handleSwitchChange("border")}
                                    value="switchBorder"
                                    color="primary"
                                />
                            } label={strings.editor_border}/>
                            <div className={classes.spacer12}/>
                            <Typography className={classes.colorCaption} variant="caption">
                                {strings.editor_background_color}
                            </Typography>
                            <CustomColorPicker
                                color={this.state.mainBackgroundColor}
                                className={classes.colorPicker}
                                onChangeComplete={this.handleColorPickerChangeComplete("mainBackgroundColor")}
                            />
                            <div className={classes.spacer12}/>
                            <Typography className={classes.colorCaption} variant="caption">
                                {strings.editor_text_color}
                            </Typography>
                            <CustomColorPicker
                                color={this.state.mainTextColor}
                                className={classes.colorPicker}
                                onChangeComplete={this.handleColorPickerChangeComplete("mainTextColor")}
                            />
                        </FormGroup>
                    </FormControl>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    };

    getExpansionPanelHeader() {
        const {classes} = this.props;

        return (
            <ExpansionPanel
                expanded={this.state.expanded === "panelContent"}
                onChange={this.handleExpansionChange("panelContent")}
            >
                <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="headline" className={classes.heading}>
                        {strings.editor_header}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <TextField
                                label={strings.editor_heading}
                                value={this.state.config.content.heading}
                                onChange={this.handleTextFieldChange("content", "heading")}
                                className={classes.textField}
                            />
                            <TextField
                                label={strings.editor_subheading}
                                value={this.state.config.content.subHeading}
                                onChange={this.handleTextFieldChange("content", "subHeading")}
                                className={classes.textField}
                            />
                        </FormGroup>
                    </FormControl>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    };

    getExpansionPanelDescription() {
        const {classes} = this.props;

        return (
            <ExpansionPanel
                expanded={this.state.expanded === "panelDescription"}
                onChange={this.handleExpansionChange("panelDescription")}
            >
                <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="headline" className={classes.heading}>
                        {strings.editor_description}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <TextField
                                label={strings.editor_text}
                                value={this.state.config.content.companyText}
                                multiline
                                onChange={this.handleTextFieldChange("content", "companyText")}
                                className={classes.textField}
                            />
                            <FormControlLabel control={
                                <Switch
                                    checked={this.state.config.appearance.companyText}
                                    onChange={this.handleSwitchChange("companyText")}
                                    value="switchCompanyText"
                                    color="primary"
                                />
                            } label={strings.editor_visible}/>
                        </FormGroup>
                    </FormControl>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    };

    getExpansionPanelPrivacy() {
        const {classes} = this.props;

        return (
            <ExpansionPanel
                expanded={this.state.expanded === "panelPrivacy"}
                onChange={this.handleExpansionChange("panelPrivacy")}
            >
                <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="headline" className={classes.heading}>
                        {strings.editor_privacy}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <FormControlLabel
                                className={classes.switchPrivacyVisible}
                                label={strings.editor_visible}
                                style={{display: this.state.messengerData.adv_needed === "1" ? "none" : ""}}
                                control={
                                    <Switch
                                        checked={this.state.config.checkbox.visible}
                                        onChange={this.handleSwitchChange("checkbox")}
                                        value="checkbox"
                                        color="primary"
                                    />
                                }
                            />
                            <TextField
                                label={strings.editor_url}
                                value={this.state.config.privacy.statementUrl}
                                onChange={this.handleTextFieldChange("privacy", "statementUrl")}
                                className={classes.textField}
                                error={this.state.config.privacy.statementUrl.length === 0}
                            />
                            <TextField
                                label={strings.editor_checkbox_hint}
                                value={this.state.config.checkbox.notCheckedText}
                                onChange={this.handleTextFieldChange("checkbox", "notCheckedText")}
                                className={classes.textField}
                                multiline
                                rows={3}
                            />
                            <TextField
                                label={strings.editor_checkbox_text}
                                value={this.state.config.checkbox.text}
                                onChange={this.handleTextFieldChange("checkbox", "text")}
                                className={classes.textField}
                            />
                        </FormGroup>
                    </FormControl>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    };

    getExpansionPanelMenu() {
        const {classes} = this.props;

        return (
            <ExpansionPanel
                expanded={this.state.expanded === "panelMenu"}
                onChange={this.handleExpansionChange("panelMenu")}
            >
                <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="headline" className={classes.heading}>
                        {strings.editor_menu}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <FormControl className={classes.select}>
                                <InputLabel htmlFor="selectMenuItem">
                                    {strings.editor_menu_item}
                                </InputLabel>
                                <Select
                                    value={this.state.menuItem}
                                    onChange={this.handleSelectChange("menuItem")}
                                    inputProps={{name: "menuItem", id: "selectMenuItem"}}
                                >
                                    {this.state.config.menuItems.map(element => (
                                        <MenuItem key={element.id} value={element.id}>
                                            {element.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <div className={classes.spacer16}/>
                            <TextField
                                label={strings.editor_name}
                                value={this.state.config.menuItems[this.state.menuItem].name}
                                onChange={this.handleTextFieldChange("menuItems", "name")}
                                className={classes.textField}
                            />
                            <TextField
                                label={strings.editor_url}
                                value={this.state.config.menuItems[this.state.menuItem].url}
                                onChange={this.handleTextFieldChange("menuItems", "url")}
                                className={classes.textField}
                            />
                            <div className={classes.buttonContainer}>
                                <Button
                                    color="primary"
                                    onClick={this.handleDeleteMenuItemClick}
                                    disabled={!(this.state.config.menuItems.length > 1)}
                                >
                                    {strings.editor_delete}
                                </Button>
                                <Button color="primary" onClick={this.handleAddMenuItemClick}
                                >
                                    {strings.editor_add}
                                </Button>
                            </div>
                        </FormGroup>
                    </FormControl>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    };

    getWidgetFrame() {
        const {classes} = this.props;

        let copyrightName = "MessengerPeople";
        let copyrightLink = "https://www.messengerpeople.com/";
        if (this.state.messengerData.aboutname !== null && this.state.messengerData.abouturl !== null) {
            copyrightName = this.state.messengerData.aboutname;
            copyrightLink = this.state.messengerData.abouturl;
        }

        return (
            <div>
                <Typography variant="display1" gutterBottom color="primary" className={classes.widgetPreviewHeading}>
                    {strings.editor_widget_preview}
                </Typography>
                <div style={{
                    height: this.state.config.appearance.height,
                    width: this.state.config.appearance.width,
                    overflowY: "auto",
                    padding: 6
                }}>
                    <Widget
                        config={this.state.config}
                        phoneNumbers={this.state.phoneNumbers}
                        messengerData={this.state.messengerData}
                        defaultData={this.props.defaultData}
                        widgetHash={this.state.widgetHash}
                    />
                </div>
                <a
                    href={copyrightLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{
                        textDecoration: "none",
                        textAlign: "right",
                        paddingRight: 6,
                        display: "block",
                        fontSize: 11,
                        color: "rgba(8, 0, 0, 0.54)"
                    }}
                >
                    © {copyrightName}
                </a>
            </div>
        );
    };

    getRightSidebar() {
        const {classes} = this.props;

        let numberDistribution = this.state.numberDistribution;
        if (this.state.waDesktopMobile === 2) {
            numberDistribution += 1;
        }

        return (
            <Card className={classes.card} style={{overflow: "visible"}}>
                <CardContent>
                    <Typography variant="display1" gutterBottom color="primary">
                        {strings.editor_messenger}
                    </Typography>
                    <FormControl className={classes.formControl} component="fieldset">
                        <FormGroup>
                            <FormControl className={classes.select}>
                                <InputLabel htmlFor="selectMessenger">
                                    {strings.editor_select}
                                </InputLabel>
                                <Select
                                    value={this.state.messenger}
                                    onChange={this.handleSelectChange("messenger")}
                                    inputProps={{name: "messenger", id: "selectMessenger",}}
                                >
                                    {Utility.getEnabledMessenger(
                                        this.state.config,
                                        this.state.messengerData
                                    ).map((element, index) =>
                                        <MenuItem key={index} value={index}>
                                            {element.name}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </FormGroup>
                        <div className={classes.spacer16}/>
                        <Typography variant="headline" gutterBottom>
                            {strings.editor_settings}
                        </Typography>
                        <FormGroup>
                            <FormControl className={classes.formControl} component="fieldset">
                                <FormGroup>
                                    <FormControlLabel
                                        className={classes.switchMessengerVisible}
                                        label={strings.editor_visible}
                                        control={
                                            <Switch
                                                checked={this.state.config.messenger[this.state.messenger].visible}
                                                onChange={this.handleSwitchChange("messengerVisible")}
                                                value="switchMessengerVisible"
                                                color="primary"
                                            />
                                        }
                                    />
                                    <TextField
                                        label={strings.editor_name}
                                        value={this.state.config.messenger[this.state.messenger].name}
                                        className={classes.textField}
                                        onChange={this.handleTextFieldChange("messenger", "name")}
                                    />
                                    <FormControl
                                        className={classes.select}
                                        style={{display: this.state.messenger === 0 ? "flex" : "none"}}>
                                        <InputLabel htmlFor="selectNumberDistribution">
                                            {strings.editor_type_of_number_distribution}
                                        </InputLabel>
                                        <Select
                                            value={this.state.numberDistribution}
                                            onChange={this.handleSelectChange("numberDistribution")}
                                            inputProps={{name: "numberDistribution", id: "selectNumberDistribution"}}
                                        >
                                            <MenuItem value={0}>
                                                {strings.editor_number_distribution_direct}
                                            </MenuItem>
                                            <MenuItem value={1}>
                                                {strings.editor_number_distribution_link}
                                            </MenuItem>
                                            {/* TODO remove */}
                                            <MenuItem
                                                value={3}
                                                disabled={this.state.config.messenger[this.state.messenger].numberDistributionStartMessages.length === 2}
                                            >
                                                {strings.editor_number_distribution_vcard}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div
                                        className={classes.spacer16}
                                        style={{display: this.state.messenger === 0 ? "flex" : "none"}}
                                    />
                                    <TextField
                                        label={strings.editor_start_message}
                                        value={this.state.config.messenger[this.state.messenger]
                                            .numberDistributionStartMessages[numberDistribution]}
                                        className={classes.textField}
                                        onChange=
                                            {this.handleTextFieldChange("messenger", "numberDistributionStartMessages")}
                                        style={{display: this.state.messenger < 2 ? "flex" : "none"}}
                                    />
                                    <FormControl
                                        className={classes.select}
                                        style={{
                                            display: this.state.messenger === 0 && this.state.numberDistribution === 1
                                                ? "flex" : "none"
                                        }}
                                    >
                                        <InputLabel htmlFor="selectWaDesktopMobileText">
                                            {strings.editor_desktop_mobile_text}
                                        </InputLabel>
                                        <Select
                                            value={this.state.waDesktopMobile}
                                            onChange={this.handleSelectChange("waDesktopMobile")}
                                            inputProps={{name: "waDesktopMobile", id: "selectWaDesktopMobileText"}}
                                            disabled={this.state.config.messenger[this.state.messenger].descriptions.length === 2}
                                        >
                                            <MenuItem value={1}>
                                                {strings.editor_desktop}
                                            </MenuItem>
                                            <MenuItem value={2}>
                                                {strings.editor_mobile}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div
                                        className={classes.spacer12}
                                        style={{
                                            display: this.state.messenger === 0 && this.state.numberDistribution === 1
                                                ? "flex" : "none"
                                        }}
                                    />
                                    <div className={classes.draftEditorContainer}>
                                        <Editor
                                            onEditorStateChange={this.handleEditorStateChange}
                                            editorState={this.state.editorState}
                                            wrapperClassName={classes.draftEditorWrapper}
                                            editorClassName={classes.draftEditor}
                                            localization={{locale: this.state.language}}
                                            toolbarCustomButtons={[
                                                <CustomEditorOption openDialog={this.handleOpenDialog}/>
                                            ]}
                                            toolbar={{
                                                options: ["link", "inline", "list", "textAlign", "fontSize", "history"],
                                                inline: {inDropdown: true},
                                                list: {inDropdown: true},
                                                textAlign: {inDropdown: true},
                                                link: {inDropdown: true},
                                            }}
                                            mention={{
                                                separator: " ",
                                                trigger: "%",
                                                suggestions: [
                                                    {text: "number", value: "number%"},
                                                    {text: "start", value: "start%"},
                                                    {text: "facebook", value: "facebook%"},
                                                    {text: "telegram", value: "telegram%"},
                                                ],
                                            }}
                                        />
                                    </div>
                                    <TextField
                                        label={strings.editor_button_text}
                                        value={this.state.config.messenger[this.state.messenger]
                                            .distributionButtonTexts[this.state.numberDistribution]}
                                        className={classes.textField}
                                        onChange={this.handleTextFieldChange("messenger", "buttonText")}
                                        style={{
                                            display: this.state.config.messenger[this.state.messenger]
                                                .distributionButtonTexts.length > 0 ? "flex" : "none"
                                        }}
                                    />
                                    <Typography className={classes.colorCaption} variant="caption">
                                        {strings.editor_color}
                                    </Typography>
                                    <CustomColorPicker
                                        color={this.state.messengerColor}
                                        className={classes.colorPicker}
                                        onChangeComplete={this.handleColorPickerChangeComplete("color")}
                                    />
                                    <div className={classes.spacer12}/>
                                    <Typography className={classes.colorCaption} variant="caption">
                                        {strings.editor_text_color}
                                    </Typography>
                                    <CustomColorPicker
                                        color={this.state.messengerTextColor}
                                        className={classes.colorPicker}
                                        onChangeComplete={this.handleColorPickerChangeComplete("textColor")}
                                    />
                                </FormGroup>
                            </FormControl>
                        </FormGroup>
                    </FormControl>
                </CardContent>
            </Card>
        );
    };

    getSaveButton() {
        const {classes} = this.props;
        return (
            <div className={classes.fixedContainer}>
                <Tooltip title={strings.editor_save} placement="top">
                    <Button variant="fab" color="primary" onClick={this.handleWidgetSaveClick}>
                        <Save/>
                    </Button>
                </Tooltip>
            </div>
        );
    };

    getWidgetEditor() {
        const {classes} = this.props;

        return (
            <div style={{padding: 16}}>
                <Grid className={classes.grid} spacing={16} container justify="space-between">
                    <Grid className={classes.editor} item xs>
                        {this.getLeftSidebar()}
                    </Grid>
                    <Grid item>
                        {this.getWidgetFrame()}
                    </Grid>
                    <Grid className={classes.messenger} item xs>
                        {this.getRightSidebar()}
                    </Grid>
                </Grid>
            </div>
        );
    };

    /* ------ RENDER -------- */

    render() {
        const {classes} = this.props;

        return (
            <div className={classes.root}>
                {this.getHelpDialog()}
                {this.getSnackbar()}
                {this.getWidgetEditor()}
                {this.getSaveButton()}
            </div>
        );
    };
}

WidgetEditor.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(WidgetEditor);



// WEBPACK FOOTER //
// ./src/components/WidgetEditor.js